import type { KbSource } from '@af/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmoothMarkdown } from '@/components/chat/SmoothMarkdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { usd } from '@/lib/format';
import { helpAnswer } from '@/lib/help-answer';
import { streamKb, useKbLocale } from '@/lib/kb';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { HelpSources } from './HelpAnswer';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  sources?: KbSource[];
  notice?: string;
  streaming?: boolean;
  complete?: boolean;
  costUsd?: number;
}

const STORE = 'af:help-chat';
const CONVERSATION_STORE = `${STORE}:conversation`;

function conversationId() {
  let existing: string | null | undefined;
  try {
    existing = globalThis.sessionStorage?.getItem(CONVERSATION_STORE);
  } catch {
    /* optional local persistence */
  }
  if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
  const id = crypto.randomUUID();
  try {
    globalThis.sessionStorage?.setItem(CONVERSATION_STORE, id);
  } catch {
    /* optional local persistence */
  }
  return id;
}
const REASON = {
  no_key: 'help.aiNoKey',
  rate_limited: 'help.aiLimit',
  budget: 'help.aiBudget',
  error: 'help.chatError',
} as const;

function load(): Msg[] {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORE);
    return raw
      ? (JSON.parse(raw) as Msg[]).map((m) => ({
          ...m,
          streaming: false,
          ...(m.role === 'assistant' && m.content && !m.complete && !m.notice
            ? { notice: 'help.incomplete' }
            : {}),
        }))
      : [];
  } catch {
    return [];
  }
}

/** Chatbot grounded on the Help Center: one capped Haiku call per question (see /api/kb/chat). */
export function HelpChat({ className, autoFocus = false }: { className?: string; autoFocus?: boolean }) {
  const { t } = useTranslation();
  const locale = useKbLocale();
  const [msgs, setMsgs] = useState<Msg[]>(load);
  const [conversation] = useState(conversationId);
  // The platform pays for help answers: its admins see what each one cost.
  const platformAdmin = useMe().data?.platformAdmin === true;
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      globalThis.sessionStorage?.setItem(STORE, JSON.stringify(msgs.slice(-30)));
    } catch {
      /* ignore */
    }
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [msgs]);
  useEffect(() => () => abort.current?.abort(), []);

  const patchLast = (f: (m: Msg) => Msg) =>
    setMsgs((all) => all.map((m, i) => (i === all.length - 1 ? f(m) : m)));

  const send = async (retryAt?: number) => {
    const retry = retryAt === undefined ? undefined : msgs[retryAt - 1];
    const text = retry?.content ?? input.trim();
    if (!text || busy) return;
    const previous = retry ? msgs.slice(0, retryAt! - 1) : msgs;
    const history = [...previous.filter((m) => m.content), { role: 'user' as const, content: text }];
    setMsgs([...history, { role: 'assistant', content: '', streaming: true }]);
    if (!retry) setInput('');
    setBusy(true);
    const ac = new AbortController();
    abort.current = ac;
    try {
      await streamKb(
        '/api/kb/chat',
        {
          locale,
          conversationId: conversation,
          // Earlier turns are read from the stored conversation on the server.
          messages: [{ role: 'user' as const, content: text }],
        },
        (e) => {
          if (e.type === 'sources') patchLast((m) => ({ ...m, sources: e.sources }));
          else if (e.type === 'text') patchLast((m) => ({ ...m, content: m.content + e.text }));
          else if (e.type === 'incomplete')
            patchLast((m) => ({ ...m, notice: t('help.incomplete'), costUsd: e.costUsd }));
          else if (e.type === 'unavailable') patchLast((m) => ({ ...m, notice: t(REASON[e.reason]) }));
          else if (e.type === 'done') patchLast((m) => ({ ...m, complete: true, costUsd: e.costUsd }));
        },
        ac.signal,
      );
    } catch {
      patchLast((m) => ({ ...m, notice: t(m.content ? 'help.incomplete' : 'help.chatError') }));
    } finally {
      patchLast((m) => ({
        ...m,
        streaming: false,
        ...(!helpAnswer(m.content, m.sources ?? []).content.trim() && !m.notice
          ? { notice: t('help.chatError') }
          : {}),
      }));
      setBusy(false);
    }
  };

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {msgs.length === 0 ? <p className="text-sm text-muted">{t('help.chatHint')}</p> : null}
        {msgs.map((m, i) => {
          const answer = helpAnswer(m.content, m.sources ?? []);
          const notice =
            (m.notice === 'help.incomplete' ? t(m.notice) : m.notice) ||
            (!m.streaming && !answer.content.trim() ? t('help.chatError') : undefined);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: append-only message list
            <div key={`${i}-${m.role}`} className={cn('flex', m.role === 'user' && 'justify-end')}>
              <div
                className={cn(
                  'max-w-[92%] rounded-2xl px-3 py-2 text-sm',
                  m.role === 'user' ? 'bg-panel-2 text-fg' : 'border border-border bg-panel text-fg-2',
                )}
              >
                {m.role === 'user' ? (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                ) : (
                  <>
                    {m.content ? <SmoothMarkdown text={answer.content} live={!!m.streaming} /> : null}
                    {m.streaming && !m.content ? (
                      <span className="text-xs text-muted">{t('help.asking')}</span>
                    ) : null}
                    {notice ? (
                      <p role="status" className="text-xs text-muted">
                        {notice}
                      </p>
                    ) : null}
                    {notice && !busy && i === msgs.length - 1 && msgs[i - 1]?.role === 'user' ? (
                      <button
                        type="button"
                        onClick={() => void send(i)}
                        className="mt-2 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-hover"
                      >
                        {t('help.chatRetry')}
                      </button>
                    ) : null}
                    {platformAdmin && !m.streaming && m.costUsd ? (
                      <p className="mt-1 text-[11px] tabular-nums text-muted">{usd(m.costUsd, 4)}</p>
                    ) : null}
                    {!m.streaming ? (
                      <HelpSources
                        sources={notice ? (m.sources ?? []) : answer.sources}
                        related={!!notice}
                        className="mt-3"
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form
        className="border-t border-border p-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            autoFocus={autoFocus}
            rows={1}
            maxLength={1000}
            placeholder={t('help.chatPlaceholder')}
            aria-label={t('help.chatPlaceholder')}
            className="max-h-32 min-h-9 flex-1 resize-none"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button type="submit" variant="primary" size="md" disabled={!input.trim() || busy}>
            {t('help.chatSend')}
          </Button>
        </div>
        <p className="mt-1.5 text-2xs text-faint">{t('help.chatDisclaimer')}</p>
      </form>
    </div>
  );
}

export function clearHelpChat() {
  try {
    globalThis.sessionStorage?.removeItem(STORE);
    globalThis.sessionStorage?.removeItem(CONVERSATION_STORE);
  } catch {
    /* ignore */
  }
}
